import { defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, Fragment as _Fragment, renderList as _renderList } from "vue";
const _hoisted_1 = {
    class: "px-6 pb-8 flex flex-col flex-1 min-h-0",
    "data-testid": "donation-detail-view"
};
const _hoisted_2 = { class: "mb-4" };
const _hoisted_3 = {
    key: 0,
    class: "animate-pulse space-y-3"
};
const _hoisted_4 = {
    key: 1,
    class: "flex items-start justify-between gap-4"
};
const _hoisted_5 = {
    class: "text-[1.75rem] font-bold leading-tight",
    "data-testid": "donation-detail-title"
};
const _hoisted_6 = { class: "mt-1 flex items-center gap-2" };
const _hoisted_7 = { class: "flex items-center gap-2 shrink-0" };
const _hoisted_8 = { class: "flex items-center justify-between mb-6" };
const _hoisted_9 = { class: "pb-1" };
const _hoisted_10 = { key: 0 };
const _hoisted_11 = ["id", "aria-labelledby"];
import { Badge, Button, ButtonGroup, buttonGroupTabId, TextLink, ConfirmDeleteModal, Dropdown, FormSaveButton, Tooltip, UnsavedChangesModal, } from "@eudonet/opal";
import { computed, reactive, ref, watch, onMounted, onUnmounted, useId } from "vue";
import { RouterLink, useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import { getAnnualTaxReceiptStatus, getDonationAllocations, generateTaxReceipt, getTaxReceiptByDonation, } from "../../infrastructure/api/donationApi";
import { getDonationDeletionBlocker, isDonationStatus, } from "../../domain/entities/Donation";
import { usePermissions } from "@/shared/composables/usePermissions";
import { useDirtyState } from "@/shared/composables/forms/useDirtyState";
import { useDonorReceiptInformation } from "../composables/useDonorReceiptInformation";
import { useAppToast } from "@/shared/composables/useAppToast";
import { getApiErrorCode, getApiErrorResult } from "@/infrastructure/services/ApiErrorService";
import { describeDonorInformationGap, parseDonorInformationGaps, } from "../../domain/entities/DonorReceiptInformation";
import { logger } from "@/infrastructure/services/LoggerService";
import { handleNavigationFailure } from "@/shared/utils/navigation";
import { donationStatusTheme as statusTheme, donationStatusIcon as statusIcon, } from "../composables/donationStatusBadge";
import { useDonationStore } from "../../stores/useDonationStore";
import { fetchDonationHistory } from "../../infrastructure/adapters/donationService";
import { mapDonationToUpdatePayload } from "../../infrastructure/mappers/donationMapper";
import DonationTabResume from "../components/donation-detail/DonationTabResume.vue";
import DonationTabDetails from "../components/donation-detail/DonationTabDetails.vue";
import DonorReceiptInformationNotice from "../components/DonorReceiptInformationNotice.vue";
import DonationTabAllocations from "../components/donation-detail/DonationTabAllocations.vue";
import DonationTabTaxReceipt from "../components/donation-detail/DonationTabTaxReceipt.vue";
import DonationTabNotes from "../components/donation-detail/DonationTabNotes.vue";
import DonationTabHistory from "../components/donation-detail/DonationTabHistory.vue";
import DonationDeleteBlockedModal from "../components/donation-detail/DonationDeleteBlockedModal.vue";
const RECEIPT_POLL_MAX_ATTEMPTS = 6;
const RECEIPT_POLL_INTERVAL_MS = 1500;
const DEFAULT_TAB = "resume";
export default /*@__PURE__*/ _defineComponent({
    __name: 'DonationDetailView',
    setup(__props) {
        const { t } = useI18n();
        const router = useRouter();
        const route = useRoute();
        const donationStore = useDonationStore();
        const { toastError, toastSuccess } = useAppToast();
        const { can, isReady: permissionsReady } = usePermissions();
        // ─── Load from BFF ────────────────────────────────────────────────────────────
        async function loadForDonation(donationId) {
            if (!donationId)
                return;
            // loadDonation fires the available-transitions fetch itself (fire-and-forget, parallel with
            // the donation fetch) — see useDonationStore.
            await donationStore.loadDonation(donationId);
            if (donationStore.currentDonation) {
                Object.assign(state, donationStore.currentDonation);
                markAsSaved();
            }
            // The tax receipt drives the lock guard: once an active receipt is issued the fiscal fields
            // (status, amount, date…) go read-only. fetchTaxReceipt logs on failure (R47) — a swallowed error
            // here would silently unlock an actually-locked donation with no diagnostic trace.
            taxReceiptData.value = await fetchTaxReceipt(donationId);
            // A one-off donation already Validated but without an active receipt may still be inside the
            // asynchronous receipt-generation window (e.g. opened right after a back-office "create as
            // Validated"). Poll so the lock surfaces without a manual page refresh.
            pollTaxReceiptWhilePending(donationId);
        }
        onMounted(() => loadForDonation(route.params.donationId));
        onUnmounted(() => {
            // Cancel any in-flight receipt poll so its pending timer can't fire after teardown.
            taxReceiptSyncSeq++;
            donationStore.clearCurrentDonation();
        });
        // ─── Reactive form state (populated by BFF on load) ─────────────────────────
        const state = reactive({});
        // Donor aggregate stats for the "Statistiques donateurs" widget — sourced from the donation's
        // donorStats (OPDON donor RFM aggregate, loaded on the detail read). Falls back to zeros until
        // the donation is loaded or when the donor has no validated donations yet.
        const donorStats = computed(() => ({
            totalDonations: state.donorStats?.totalDonatedAmount ?? 0,
            // The stats are in the workspace currency, not the displayed donation's. The fallback only covers
            // an OPDON that sends none (the store carries the currency over a save).
            currency: state.donorStats?.currency ?? state.currency,
            donationCount: state.donorStats?.donationCount ?? 0,
            averageDonation: state.donorStats?.averageDonation ?? 0,
            firstDonationDate: state.donorStats?.firstDonationDate ?? "",
        }));
        // Change history — loaded lazily from the BFF the first time the History tab is
        // opened (OP-5075). One audit entry can flatten into several rows (one per field).
        const changeHistory = ref([]);
        const isLoadingHistory = ref(false);
        const allocations = ref({
            items: [],
            totalAllocated: 0,
            totalReceipt: 0,
            totalUnallocated: 0,
            isFullyAllocated: false,
        });
        const isLoadingAllocations = ref(false);
        const isDonationFullyAllocated = computed(() => allocations.value.isFullyAllocated);
        const sectionOpen = reactive({ donation: true, donor: true, campaign: true });
        // ─── Save / delete ───────────────────────────────────────────────────────────
        const isSaving = ref(false);
        const isDeleting = ref(false);
        const isGeneratingReceipt = ref(false);
        // Bumped after a manual generate so the Tax receipt tab remounts and re-fetches the new receipt.
        const taxReceiptKey = ref(0);
        // Loaded on donation load and after generation — drives isDonationLocked (OP-5849).
        const taxReceiptData = ref(null);
        // Validating a one-off donation triggers asynchronous PerDonation receipt generation on the
        // backend (validation event → receipt-generation request → GenerateTaxReceiptCommand), which then
        // locks the donation. The receipt isn't queryable the instant a save returns, so after a load or a
        // save the lock guard is polled through that window rather than read once. Tenants in Annual/None
        // receipt mode generate nothing, so the poll just exhausts its bounded attempts and the donation
        // stays unlocked. A monotonic token cancels an in-flight poll on a newer load/save, a donation
        // switch, or unmount.
        let taxReceiptSyncSeq = 0;
        // errorCode → i18n key for the manual "Generate tax receipt" action.
        // The button is disabled client-side for the "not validated" and "receipt already exists" states,
        // so the conflict a user actually hits when clicking Generate is the issuer-legal-info-missing one
        // (OPDON 409 TaxReceiptIssuerInfoMissing) — or, when OPUS is reachable-but-erroring, the 502
        // OpusLegalInfoUnavailable. Each maps to its own actionable message instead of the generic fallback.
        // TaxReceipt.GenerationInProgress: another tab (or the validation-triggered run) is generating the
        // same donation's receipt; the per-tab button guard cannot see it (OP-8894).
        const GENERATE_RECEIPT_ERROR_KEYS = {
            TaxReceiptGenerationConflict: "finance.donations.taxReceipt.generateErrorConflict",
            "TaxReceipt.GenerationInProgress": "finance.donations.taxReceipt.generateErrorInProgress",
            TaxReceiptIssuerInfoMissing: "finance.donations.taxReceipt.generateErrorIssuerInfoMissing",
            OpusLegalInfoUnavailable: "finance.donations.taxReceipt.generateErrorIssuerUnavailable",
        };
        // errorCode → deletion blocker. OPDON answers 409 with one of these when the deletion
        // rule refuses the delete; each maps to the explanation the M-E-01 modal shows.
        const DELETE_ERROR_BLOCKERS = {
            "Donation.Deletion.StatusLocked": "StatusLocked",
            "Donation.Deletion.TaxReceiptLinked": "TaxReceiptLinked",
        };
        // errorCode → i18n key for a failed save (handleSave). Required-field clears are prevented
        // client-side by `canSave`, so they don't reach the backend; everything else falls back to the
        // generic message.
        const SAVE_ERROR_KEYS = {
            // Changing the donor or a fiscal field on a donation locked by an active receipt returns 409.
            "Donation.TaxReceiptLocked": "finance.donations.detail.saveLockedError",
            // Changing the donor and the subscription link in one save is rejected by the backend — tell the
            // user to do them one at a time instead of showing the generic save-error toast.
            "Donation.DonorSubscription.Conflict": "finance.donations.detail.saveDonorSubscriptionConflict",
            // OP-7954: re-pointing the donation onto a donor that cannot be receipted. Only reached when the
            // refusal carries no readable gap list — otherwise the catch names the missing fields itself.
            "Donation.Donor.ReceiptInformationMissing": "finance.donations.detail.saveDonorInformationMissing",
        };
        // ─── Tax-receipt guards (OP-5847, OP-5849, OP-5853) ──────────────────────────
        const currentDonationStatus = computed(() => donationStore.currentDonation?.status ?? state.status);
        const currentDonationType = computed(() => donationStore.currentDonation?.donationType ?? state.donationType);
        /** True for OneOff donations; false for Regular (subscription-linked) ones. */
        const isOneOffDonation = computed(() => currentDonationType.value !== "Regular");
        /** Only an ACTIVE (non-Cancelled) receipt locks the donation. `TaxReceipt.Cancel` keeps the row
         *  (status → Cancelled) and `GetTaxReceiptByDonationQuery` returns it unfiltered, but a Cancelled
         *  receipt no longer represents an issued fiscal document — so a donation whose only receipt is
         *  Cancelled is treated as having none: editing is unlocked AND a new receipt can be generated again.
         *  Mirrors the backend lock guard, the regeneration guard and the partial unique index, which all
         *  exclude only Cancelled. */
        const isDonationLocked = computed(() => taxReceiptData.value !== null && taxReceiptData.value?.status !== "Cancelled");
        /** Receipt can only be generated for validated one-off donations that don't already have an
         *  active (non-Cancelled) receipt (OP-5847, OP-5849, OP-5853). */
        const canGenerateReceipt = computed(() => isOneOffDonation.value && currentDonationStatus.value === "Validated" && !isDonationLocked.value);
        const showDeleteModal = ref(false);
        const showDeleteBlockedModal = ref(false);
        /** Reason shown in the M-E-01 modal; set from the client-side rule or from a 409 of the backend. */
        const deleteBlocker = ref(null);
        /** Whether the current user may delete donations at all (CTA-18 lock otherwise). `can()` fails
         *  closed until the profile resolves, so the padlock is only meaningful once `permissionsReady`. */
        const canEditDonation = computed(() => can("opdon.edit"));
        /** Deletion rule, evaluated client-side from the loaded status and the receipt row.
         *  Any receipt covering the donation counts, unit or annual, cancelled or not (a cancelled receipt
         *  is kept on record and still blocks deletion, unlike the edit lock). `fetchTaxReceipt` and the
         *  Tax receipt tab (`onReceiptUpdated`) see the unit receipt whatever its status, but of the annual
         *  receipts only the active one or the cancelled one still awaiting its replacement: a donation
         *  listed on a cancelled annual receipt that was regenerated without it (the donation is Canceled
         *  by then) looks receipt-free here,
         *  reaches M-W-05 and is refused by the server with 409 — routed to M-E-01 by `handleDelete`, so
         *  nothing is deleted, the explanation just comes one step later. Closing that gap takes a
         *  server-side deletion verdict on the donation read model, not a client reconstruction.
         *  Null while the status is unknown (still loading, or a status this build does not know) — same
         *  server fallback. */
        const clientDeleteBlocker = computed(() => {
            const status = currentDonationStatus.value;
            if (!status || !isDonationStatus(status))
                return null;
            return getDonationDeletionBlocker(status, taxReceiptData.value !== null);
        });
        /** Fetches the receipt covering the donation: its unit receipt, else the annual receipt that lists
         *  it (OP-7612) — active (`Covered`, which locks the donation all the same) or cancelled
         *  (`Cancelled`, which leaves it editable but still holds it on record for the deletion rule). Logs
         *  on failure (R47) and returns null so a transient error leaves the donation unlocked rather than
         *  crashing the view. The deletion rule reads that null as "no receipt" too (fail-open, same as the
         *  edit lock): on that path the user may confirm M-W-05 for a delete the server then refuses with
         *  409, which lands in M-E-01 — the explanation arrives one step later, nothing is deleted. */
        async function fetchTaxReceipt(donationId) {
            try {
                const unit = await getTaxReceiptByDonation(donationId);
                if (unit && unit.status !== "Cancelled")
                    return unit;
                const annual = await getAnnualTaxReceiptStatus(donationId);
                const annualCovers = annual.status === "Covered" || annual.status === "Cancelled";
                return annualCovers && annual.receipt ? annual.receipt : unit;
            }
            catch (e) {
                logger.error("DonationDetailView: failed to load tax receipt for lock guard", e);
                return null;
            }
        }
        /** While a one-off donation is Validated and has NO receipt row yet, polls for the asynchronously
         *  generated receipt and updates taxReceiptData (→ isDonationLocked) as soon as one appears. Bounded
         *  and cancellable: a newer sync, a donation switch, or unmount supersedes it via taxReceiptSyncSeq.
         *
         *  Gated on `taxReceiptData === null` (no row at all), NOT on `!isDonationLocked`: a Cancelled receipt
         *  leaves the donation unlocked yet is NOT pending generation — the backend never auto-regenerates
         *  after a manual cancel — so polling there would loop uselessly and could even re-lock a
         *  just-cancelled donation off a lagging read. Annual/None-mode tenants also produce no row; the poll
         *  then exhausts its bounded attempts and the donation stays unlocked. */
        async function pollTaxReceiptWhilePending(donationId) {
            // Supersede any in-flight poll, even when this call decides not to poll.
            const syncId = ++taxReceiptSyncSeq;
            if (!isOneOffDonation.value ||
                currentDonationStatus.value !== "Validated" ||
                taxReceiptData.value !== null) {
                return;
            }
            for (let attempt = 0; attempt < RECEIPT_POLL_MAX_ATTEMPTS; attempt++) {
                await new Promise((resolve) => setTimeout(resolve, RECEIPT_POLL_INTERVAL_MS));
                // Superseded by a newer sync, a donation switch, or unmount → stop silently.
                if (syncId !== taxReceiptSyncSeq || route.params.donationId !== donationId)
                    return;
                const receipt = await fetchTaxReceipt(donationId);
                if (syncId !== taxReceiptSyncSeq || route.params.donationId !== donationId)
                    return;
                // Only an active (non-Cancelled) receipt locks the donation — mirror isDonationLocked.
                if (receipt && receipt.status !== "Cancelled") {
                    taxReceiptData.value = receipt;
                    return;
                }
            }
        }
        // Close the residual lock-engage race: the poll disables the fiscal inputs once an active receipt
        // lands, but a value the user already changed BEFORE the lock engaged (e.g. status → Refunded) stays
        // dirty and submittable, and the backend would reject it (DonationTaxReceiptLockException). On the
        // unlocked→locked transition, revert the now-locked fiscal fields to the persisted donation; the
        // editable fields (Note, Source du don) keep their in-flight edits. At load-time lock this is a no-op
        // (state was just synced from the persisted donation).
        watch(isDonationLocked, (locked, wasLocked) => {
            if (!locked || wasLocked)
                return;
            const persisted = donationStore.currentDonation;
            if (!persisted)
                return;
            state.status = persisted.status;
            state.amount = persisted.amount;
            state.donationDate = persisted.donationDate;
            state.paymentMethod = persisted.paymentMethod;
            state.chequeNumber = persisted.chequeNumber;
            state.subscriptionId = persisted.subscriptionId;
            // The donor is fiscally engaging too, so a receipt that lands mid-edit also reverts an in-flight
            // donor re-point. Campaign stays editable under lock, so its in-flight edit is intentionally kept.
            state.donorType = persisted.donorType;
            state.donorName = persisted.donorName;
            state.contactId = persisted.contactId;
            state.contactDisplayName = persisted.contactDisplayName;
            state.organizationId = persisted.organizationId;
        });
        // ─── Dirty state ─────────────────────────────────────────────────────────────
        const showUnsavedChangesModal = ref(false);
        const pendingTabKey = ref(null);
        const pendingTabQuery = ref({});
        const pendingRouteCallbacks = {
            proceed: null,
            cancel: null,
        };
        const { isDirty, markAsSaved, resetToOriginal } = useDirtyState({
            data: state,
            onNavigationBlocked: (proceed, cancel) => {
                showUnsavedChangesModal.value = true;
                pendingRouteCallbacks.proceed = proceed;
                pendingRouteCallbacks.cancel = cancel;
            },
            enableBeforeUnload: true,
        });
        // ─── Donor completeness (OP-7954) ────────────────────────────────────────────
        /**
         * True while the edited donor differs from the persisted one. The backend only re-checks
         * completeness when the donor actually moves (`contactChanged || organizationChanged`), so
         * checking on any other edit would block a note or an amount fix on someone else's incomplete
         * record.
         */
        const donorChanged = computed(() => {
            const saved = donationStore.currentDonation;
            // `state` is filled from the store a tick after mount; comparing before that makes every
            // donation look re-pointed and fires a probe nobody asked for.
            if (!saved || !state.id)
                return false;
            return ((state.contactId ?? undefined) !== (saved.contactId ?? undefined) ||
                (state.organizationId ?? undefined) !== (saved.organizationId ?? undefined));
        });
        const receiptInformation = useDonorReceiptInformation({
            // For an organisation donation the contact is the referent, which is exactly what the check
            // expects as `referentContactId` — same shape as the creation modal.
            contactId: computed(() => state.contactId || undefined),
            organizationId: computed(() => state.donorType === "Organization" ? state.organizationId || undefined : undefined),
            enabled: donorChanged,
        });
        // Save is allowed only when dirty AND no previously-set required reference has been cleared.
        // The donor re-point pickers (and the campaign picker) let the user empty a field; the backend
        // rejects clearing a set donor/campaign, so we block the save client-side instead of round-tripping
        // to a 400 — the empty picker shows its own placeholder as the inline cue. A field that was already
        // empty stays freely saveable.
        const canSave = computed(() => {
            if (!isDirty.value)
                return false;
            const saved = donationStore.currentDonation;
            if (!saved)
                return true;
            if (saved.campaignId && !state.campaignId)
                return false;
            if (saved.contactId && !state.contactId)
                return false;
            if (saved.donorType === "Organization" && saved.organizationId && !state.organizationId)
                return false;
            // A re-point onto a donor that cannot be receipted is refused by the backend (400 with the list
            // of gaps). Block it here too so the user sees WHY before clicking, as on the creation modal.
            // `isComplete` also covers the issuing workspace, which the backend checks on this same path.
            if (donorChanged.value && !receiptInformation.isComplete.value)
                return false;
            return true;
        });
        // Watch for subsequent store updates (e.g., after a save that refreshes the donation).
        watch(() => donationStore.currentDonation, (donation) => {
            if (donation) {
                Object.assign(state, donation);
                markAsSaved();
            }
        });
        // ─── Tabs ────────────────────────────────────────────────────────────────────
        const tabs = computed(() => [
            { key: "resume", label: t("finance.donations.tabs.resume") },
            { key: "details", label: t("finance.donations.tabs.details") },
            { key: "allocations", label: t("finance.donations.tabs.allocations") },
            { key: "taxReceipt", label: t("finance.donations.tabs.taxReceipt") },
            { key: "comments", label: t("finance.donations.tabs.comments") },
            { key: "history", label: t("finance.donations.tabs.history") },
        ]);
        const VALID_TABS = [
            "resume",
            "details",
            "allocations",
            "taxReceipt",
            "comments",
            "history",
        ];
        const panelId = useId();
        const activeTab = computed(() => {
            const q = route.query.tab;
            return typeof q === "string" && VALID_TABS.includes(q)
                ? q
                : DEFAULT_TAB;
        });
        // Reload comments every time the Notes tab is opened (mirrors the History tab) so
        // the list is always fresh. The store's loadComments uses a sequence token, so a
        // slow earlier response can't overwrite a newer one.
        function loadComments() {
            const donationId = route.params.donationId;
            if (!donationId)
                return;
            donationStore.loadComments(donationId).catch(() => {
                toastError("finance.donations.detail.comments.loadError");
            });
        }
        // Reload the change history every time the History tab is opened (OP-5075) — the
        // audit trail can grow from edits made elsewhere, so it should always be fresh.
        // A monotonic token prevents a slow earlier response from overwriting a newer one.
        let loadAllocationsSeq = 0;
        function loadAllocations() {
            const donationId = route.params.donationId;
            if (!donationId)
                return;
            const requestId = ++loadAllocationsSeq;
            isLoadingAllocations.value = true;
            getDonationAllocations(donationId)
                .then((data) => {
                if (requestId === loadAllocationsSeq)
                    allocations.value = data;
            })
                .catch(() => {
                if (requestId === loadAllocationsSeq)
                    toastError("finance.donations.detail.alloc.loadError");
            })
                .finally(() => {
                if (requestId === loadAllocationsSeq)
                    isLoadingAllocations.value = false;
            });
        }
        let loadHistorySeq = 0;
        function loadHistory() {
            const donationId = route.params.donationId;
            if (!donationId)
                return;
            const requestId = ++loadHistorySeq;
            isLoadingHistory.value = true;
            fetchDonationHistory(donationId)
                .then((rows) => {
                if (requestId === loadHistorySeq)
                    changeHistory.value = rows;
            })
                .catch(() => {
                if (requestId === loadHistorySeq)
                    toastError("finance.donations.detail.history.loadError");
            })
                .finally(() => {
                if (requestId === loadHistorySeq)
                    isLoadingHistory.value = false;
            });
        }
        watch(activeTab, (tab) => {
            // Resume also needs allocations for the "Répartition du don" breakdown, not just the
            // Allocations tab.
            if (tab === "allocations" || tab === "resume")
                loadAllocations();
            if (tab === "comments")
                loadComments();
            // Resume shows the most recent audit entries in "Activité récente", so it needs
            // the history too — not just the History tab.
            if (tab === "history" || tab === "resume")
                loadHistory();
        }, { immediate: true });
        // Navigating directly between two donation IDs (e.g. a deep link or related
        // record) reuses this component instance — onMounted/onUnmounted don't fire —
        // so reload the donation, reset the store, and refresh the active tab's data.
        // Declared here (after activeTab + loadComments/loadHistory) so every reference
        // resolves against an already-initialized binding rather than relying on read order.
        watch(() => route.params.donationId, (id, prevId) => {
            const donationId = id;
            if (!donationId || donationId === prevId)
                return;
            donationStore.clearCurrentDonation();
            changeHistory.value = [];
            allocations.value = {
                items: [],
                totalAllocated: 0,
                totalReceipt: 0,
                totalUnallocated: 0,
                isFullyAllocated: false,
            };
            loadForDonation(donationId);
            if (activeTab.value === "allocations")
                loadAllocations();
            if (activeTab.value === "comments")
                loadComments();
            if (activeTab.value === "history" || activeTab.value === "resume")
                loadHistory();
        });
        function navigateToTab(tabKey, extraQuery = {}) {
            // Strip both `tab` and `action` so a stale create-modal flag never carries across
            // tabs; `extraQuery` re-adds `action: 'new'` only when a quick action requests it.
            const { tab: _tab, action: _action, ...rest } = route.query;
            const nextQuery = {
                ...rest,
                ...extraQuery,
                ...(tabKey === DEFAULT_TAB ? {} : { tab: tabKey }),
            };
            router.push({ query: nextQuery }).catch(handleNavigationFailure("DonationDetailView:tab"));
        }
        // ─── Actions dropdown ────────────────────────────────────────────────────────
        // CTA-18: "Supprimer la fiche" sits in the destructive group of the Actions menu. Without the
        // edit right it stays visible but locked (padlock + the visible sub-text "Permissions
        // insuffisantes." as the item description, which OPAL wires with aria-describedby and keeps
        // focusable so the reason is reachable by keyboard). With the right it is always clickable: a
        // deletable donation opens the confirmation (M-W-05), a protected one opens the blocking
        // explanation (M-E-01). While the
        // profile has not resolved yet (deep link), the right is not known: neutral loading state rather
        // than a padlock that would wrongly tell an editor they lack the right.
        const actionsMenu = computed(() => [
            !permissionsReady.value
                ? {
                    label: t("finance.donations.detail.deleteRecord"),
                    icon: "ri-delete-bin-6-line",
                    danger: true,
                    disabled: true,
                    loading: true,
                    testId: "donation-action-delete",
                }
                : canEditDonation.value
                    ? {
                        label: t("finance.donations.detail.deleteRecord"),
                        icon: "ri-delete-bin-6-line",
                        danger: true,
                        testId: "donation-action-delete",
                        onClick: () => {
                            const blocker = clientDeleteBlocker.value;
                            if (blocker) {
                                deleteBlocker.value = blocker;
                                showDeleteBlockedModal.value = true;
                            }
                            else {
                                showDeleteModal.value = true;
                            }
                        },
                    }
                    : {
                        label: t("finance.donations.detail.deleteRecord"),
                        description: t("finance.donations.detail.deleteNoRights"),
                        icon: "ri-lock-line",
                        disabled: true,
                        testId: "donation-action-delete",
                    },
        ]);
        // ─── Handlers ────────────────────────────────────────────────────────────────
        const handleTabChange = (tabKey, extraQuery = {}) => {
            if (tabKey === activeTab.value && Object.keys(extraQuery).length === 0)
                return;
            if (isDirty.value) {
                pendingTabKey.value = tabKey;
                pendingTabQuery.value = extraQuery;
                showUnsavedChangesModal.value = true;
            }
            else {
                navigateToTab(tabKey, extraQuery);
            }
        };
        const handleSave = async () => {
            const donationId = route.params.donationId;
            // Capture the persisted status BEFORE the save so a status transition surfaces the
            // dedicated "status updated" toast (OP-5848), while any other field edit shows the
            // generic "updated" one. Read from the store (last persisted value), not from `state`,
            // which already holds the user's edited status.
            const statusChanged = state.status !== undefined && state.status !== donationStore.currentDonation?.status;
            isSaving.value = true;
            try {
                await donationStore.updateDonation(donationId, mapDonationToUpdatePayload(state));
                markAsSaved();
                toastSuccess(statusChanged
                    ? "finance.donations.detail.statusUpdateSuccess"
                    : "finance.donations.detail.saveSuccess");
                // Re-sync the lock guard: a transition to Validated triggers asynchronous receipt generation
                // that locks the donation. Without this, isDonationLocked stays stale in the same session and the
                // user can pick a fiscal change (e.g. → Refunded) the backend then rejects (DonationTaxReceiptLockException).
                taxReceiptData.value = await fetchTaxReceipt(donationId);
                pollTaxReceiptWhilePending(donationId);
            }
            catch (e) {
                // The store records the error but no component reads it — surface it to the user so a failed
                // save isn't silently mistaken for a success (toast = user feedback, R46). A fiscal-lock 409
                // (`Donation.TaxReceiptLocked`) maps to its own actionable message; anything else is generic.
                const code = getApiErrorCode(e);
                // OP-7954: re-pointing onto an incomplete donor is refused WITH the list of missing fields.
                // The creation modal names them, so the edit flow must too — same gaps, same wording, same
                // i18n keys, otherwise the two screens describe the same refusal differently.
                const gaps = parseDonorInformationGaps(getApiErrorResult(e));
                if (gaps.length > 0) {
                    toastError("finance.donations.detail.saveDonorInformationMissing", {
                        details: gaps.map(describeGap).join(" · "),
                    });
                    return;
                }
                toastError(code && SAVE_ERROR_KEYS[code] ? SAVE_ERROR_KEYS[code] : "finance.donations.detail.saveError");
            }
            finally {
                isSaving.value = false;
            }
        };
        /** Called by DonationTabTaxReceipt whenever its receipt ref changes (OP-5849 lock sync). */
        function onReceiptUpdated(r) {
            taxReceiptData.value = r;
        }
        const handleGenerateReceipt = async () => {
            const donationId = route.params.donationId;
            if (!donationId || isGeneratingReceipt.value)
                return;
            isGeneratingReceipt.value = true;
            try {
                const receipt = await generateTaxReceipt(donationId);
                taxReceiptData.value = receipt;
                toastSuccess("finance.donations.taxReceipt.generateSuccess");
                // Remount the Tax receipt tab so it re-fetches and shows the freshly generated receipt (OP-5854).
                taxReceiptKey.value += 1;
                // Navigate to the tax receipt tab so the user can immediately see the generated receipt.
                navigateToTab("taxReceipt");
            }
            catch (e) {
                const code = getApiErrorCode(e);
                // OP-7954: a refusal for missing donor/issuer information carries WHICH fields are missing.
                // Saying "generation failed" there would hide the one thing the user can act on.
                const gaps = parseDonorInformationGaps(getApiErrorResult(e));
                if (gaps.length > 0) {
                    toastError("finance.donations.taxReceipt.generateErrorInformationMissing", {
                        details: gaps.map(describeGap).join(" · "),
                    });
                    return;
                }
                toastError(code && GENERATE_RECEIPT_ERROR_KEYS[code]
                    ? GENERATE_RECEIPT_ERROR_KEYS[code]
                    : "finance.donations.taxReceipt.generateError");
            }
            finally {
                isGeneratingReceipt.value = false;
            }
        };
        /**
         * One gap as "<object> : <field>, <field>" — the identifiers come from the backend, the words
         * from the same i18n keys the creation modal uses (R29/R76), so the two screens never disagree.
         */
        /** Kept as a thin local name; the wording itself is shared with the two create modals. */
        function describeGap(gap) {
            return describeDonorInformationGap(gap, t);
        }
        const handleDelete = async () => {
            const donationId = route.params.donationId;
            isDeleting.value = true;
            try {
                await donationStore.deleteDonation(donationId);
                showDeleteModal.value = false;
                toastSuccess("finance.donations.deleteSuccess");
                router.push({ name: "DonationList" });
            }
            catch (e) {
                // The backend applies the same deletion rule. Reaching it here means the client-side
                // check was stale (a receipt generated meanwhile, a status changed in another tab) — explain
                // it with the same M-E-01 modal rather than a generic failure toast, and reload the donation
                // so the status badge, the lock and the client-side rule catch up with the server. Not when
                // the form holds unsaved edits: the reload would overwrite them with no dirty-state prompt,
                // and M-E-01 explains the refusal without it. A failed reload is logged (R47), not left as an
                // unhandled rejection — the modal already stands on its own.
                const blocker = DELETE_ERROR_BLOCKERS[getApiErrorCode(e) ?? ""];
                if (blocker) {
                    showDeleteModal.value = false;
                    deleteBlocker.value = blocker;
                    showDeleteBlockedModal.value = true;
                    if (!isDirty.value) {
                        loadForDonation(donationId).catch((reloadError) => logger.error("DonationDetailView: reload after deletion refusal failed", reloadError));
                    }
                    return;
                }
                // The store swallows the error into its own state; surface it so a failed delete
                // isn't mistaken for success (toast = user feedback, R46).
                toastError("finance.donations.deleteError");
            }
            finally {
                isDeleting.value = false;
            }
        };
        const handleStay = () => {
            showUnsavedChangesModal.value = false;
            pendingTabKey.value = null;
            pendingTabQuery.value = {};
            if (pendingRouteCallbacks.cancel) {
                pendingRouteCallbacks.cancel();
                pendingRouteCallbacks.proceed = null;
                pendingRouteCallbacks.cancel = null;
            }
        };
        // Resume whatever the guard interrupted — a queued tab switch or a real route
        // navigation. Shared by the discard path (after reverting) and the
        // save-and-continue path (after a successful persist).
        function proceedPendingNavigation() {
            if (pendingTabKey.value) {
                const target = pendingTabKey.value;
                const targetQuery = pendingTabQuery.value;
                pendingTabKey.value = null;
                pendingTabQuery.value = {};
                markAsSaved();
                navigateToTab(target, targetQuery);
            }
            if (pendingRouteCallbacks.proceed) {
                pendingRouteCallbacks.proceed();
                pendingRouteCallbacks.proceed = null;
                pendingRouteCallbacks.cancel = null;
            }
        }
        const handleLeave = () => {
            showUnsavedChangesModal.value = false;
            resetToOriginal();
            proceedPendingNavigation();
        };
        // Modal primary "Enregistrer les modifications" — persist first (handleSave owns
        // the toasts + lock re-sync), then resume the interrupted navigation only if the
        // save cleared the dirty flag. A failed save leaves the form dirty and keeps the
        // modal open for a retry.
        async function handleSaveAndContinue() {
            if (!canSave.value)
                return;
            await handleSave();
            if (isDirty.value)
                return;
            showUnsavedChangesModal.value = false;
            proceedPendingNavigation();
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                _createElementVNode("div", _hoisted_1, [
                    _createElementVNode("div", _hoisted_2, [
                        _createVNode(_unref(TextLink), {
                            as: _unref(RouterLink),
                            "icon-left": "ri-arrow-left-line",
                            to: { name: 'DonationList' },
                            class: "mb-3"
                        }, {
                            default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(_ctx.$t("finance.donations.detail.backButton")), 1 /* TEXT */)
                            ]),
                            _: 1 /* STABLE */
                        }, 8 /* PROPS */, ["as"]),
                        (_unref(donationStore).isLoading && !state.id)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [...(_cache[9] || (_cache[9] = [
                                    _createElementVNode("div", { class: "h-8 w-64 bg-gray-200 rounded" }, null, -1 /* CACHED */),
                                    _createElementVNode("div", { class: "h-5 w-32 bg-gray-100 rounded" }, null, -1 /* CACHED */)
                                ]))]))
                            : (_openBlock(), _createElementBlock("div", _hoisted_4, [
                                _createElementVNode("div", null, [
                                    _createElementVNode("h1", _hoisted_5, _toDisplayString(_unref(donationStore).currentDonation?.label ?? state.label ?? "…"), 1 /* TEXT */),
                                    _createElementVNode("div", _hoisted_6, [
                                        (_unref(donationStore).currentDonation?.status ?? state.status)
                                            ? (_openBlock(), _createBlock(_unref(Badge), {
                                                key: 0,
                                                theme: _unref(statusTheme)[(_unref(donationStore).currentDonation?.status ?? state.status)],
                                                label: _ctx.$t(`finance.donations.status.${_unref(donationStore).currentDonation?.status ?? state.status}`),
                                                "show-close-icon": false,
                                                size: "sm",
                                                "left-icon": _unref(statusIcon)[(_unref(donationStore).currentDonation?.status ?? state.status)]
                                            }, null, 8 /* PROPS */, ["theme", "label", "left-icon"]))
                                            : _createCommentVNode("v-if", true)
                                    ])
                                ]),
                                _createElementVNode("div", _hoisted_7, [
                                    (isOneOffDonation.value)
                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                            (!canGenerateReceipt.value)
                                                ? (_openBlock(), _createBlock(_unref(Tooltip), {
                                                    key: 0,
                                                    text: isDonationLocked.value
                                                        ? _ctx.$t('finance.donations.taxReceipt.generateErrorConflict')
                                                        : _ctx.$t('finance.donations.taxReceipt.notValidatedTooltip'),
                                                    position: "top",
                                                    tabindex: 0
                                                }, {
                                                    default: _withCtx(() => [
                                                        _createVNode(_unref(Button), {
                                                            variant: "primary",
                                                            "icon-left": "ri-file-text-line",
                                                            label: _ctx.$t('finance.donations.detail.generateReceipt'),
                                                            disabled: true
                                                        }, null, 8 /* PROPS */, ["label"])
                                                    ]),
                                                    _: 1 /* STABLE */
                                                }, 8 /* PROPS */, ["text"]))
                                                : (_openBlock(), _createBlock(_unref(Button), {
                                                    key: 1,
                                                    variant: "primary",
                                                    "icon-left": "ri-file-text-line",
                                                    label: _ctx.$t('finance.donations.detail.generateReceipt'),
                                                    loading: isGeneratingReceipt.value,
                                                    disabled: isGeneratingReceipt.value,
                                                    onClick: handleGenerateReceipt
                                                }, null, 8 /* PROPS */, ["label", "loading", "disabled"]))
                                        ], 64 /* STABLE_FRAGMENT */))
                                        : _createCommentVNode("v-if", true),
                                    _createVNode(_unref(Dropdown), {
                                        label: _ctx.$t('finance.donations.detail.actions'),
                                        items: actionsMenu.value,
                                        "menu-align": "right",
                                        "menu-width": "180px"
                                    }, null, 8 /* PROPS */, ["label", "items"])
                                ])
                            ]))
                    ]),
                    _createElementVNode("div", _hoisted_8, [
                        _createVNode(_unref(ButtonGroup), {
                            "model-value": activeTab.value,
                            "panel-id": _unref(panelId),
                            "overflow-label": _ctx.$t('common.tabs.next'),
                            "overflow-previous-label": _ctx.$t('common.tabs.previous'),
                            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => (handleTabChange($event)))
                        }, {
                            default: _withCtx(() => [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(tabs.value, (tab) => {
                                    return (_openBlock(), _createBlock(_unref(Button), {
                                        key: tab.key,
                                        variant: "tertiary",
                                        size: "xs",
                                        label: tab.label,
                                        value: tab.key,
                                        "data-testid": `donation-detail-tab-${tab.key}`,
                                        "data-active": tab.key === activeTab.value ? 'true' : 'false'
                                    }, null, 8 /* PROPS */, ["label", "value", "data-testid", "data-active"]));
                                }), 128 /* KEYED_FRAGMENT */))
                            ]),
                            _: 1 /* STABLE */
                        }, 8 /* PROPS */, ["model-value", "panel-id", "overflow-label", "overflow-previous-label"]),
                        _createElementVNode("div", _hoisted_9, [
                            _createVNode(_unref(FormSaveButton), {
                                enabled: canSave.value,
                                saving: isSaving.value,
                                "save-label": _ctx.$t('finance.donations.detail.saveChanges'),
                                "saving-label": _ctx.$t('finance.donations.detail.saving'),
                                "disabled-hint": _ctx.$t('form.saveDisabledHint'),
                                onSave: handleSave
                            }, null, 8 /* PROPS */, ["enabled", "saving", "save-label", "saving-label", "disabled-hint"])
                        ])
                    ]),
                    (donorChanged.value)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                            _createVNode(DonorReceiptInformationNotice, {
                                class: "mb-4",
                                gaps: _unref(receiptInformation).issuerGaps.value,
                                unavailable: false,
                                "heading-key": "finance.donations.create.receiptInformation.workspaceTitle"
                            }, null, 8 /* PROPS */, ["gaps"]),
                            _createVNode(DonorReceiptInformationNotice, {
                                class: "mb-4",
                                gaps: _unref(receiptInformation).donorGaps.value,
                                unavailable: _unref(receiptInformation).unavailable.value,
                                "contact-id": state.contactId || undefined,
                                "organization-id": state.donorType === 'Organization' ? state.organizationId || undefined : undefined
                            }, null, 8 /* PROPS */, ["gaps", "unavailable", "contact-id", "organization-id"])
                        ]))
                        : _createCommentVNode("v-if", true),
                    (state.id)
                        ? (_openBlock(), _createElementBlock("div", {
                            key: 1,
                            id: _unref(panelId),
                            role: "tabpanel",
                            "aria-labelledby": _unref(buttonGroupTabId)(_unref(panelId), activeTab.value),
                            class: "flex flex-col flex-1 min-h-0"
                        }, [
                            (activeTab.value === 'resume')
                                ? (_openBlock(), _createBlock(DonationTabResume, {
                                    key: 0,
                                    state: state,
                                    "section-open": sectionOpen,
                                    history: changeHistory.value,
                                    loading: isLoadingHistory.value,
                                    "donor-stats": donorStats.value,
                                    allocations: allocations.value.items,
                                    "is-fully-allocated": isDonationFullyAllocated.value,
                                    locked: isDonationLocked.value,
                                    onNavigateToHistory: _cache[1] || (_cache[1] = ($event) => (handleTabChange('history'))),
                                    onAddAllocation: _cache[2] || (_cache[2] = ($event) => (handleTabChange('allocations', { action: 'new' }))),
                                    onAddComment: _cache[3] || (_cache[3] = ($event) => (handleTabChange('comments'))),
                                    onNavigateToTaxReceipt: _cache[4] || (_cache[4] = ($event) => (handleTabChange('taxReceipt')))
                                }, null, 8 /* PROPS */, ["state", "section-open", "history", "loading", "donor-stats", "allocations", "is-fully-allocated", "locked"]))
                                : (activeTab.value === 'details')
                                    ? (_openBlock(), _createBlock(DonationTabDetails, {
                                        key: 1,
                                        state: state,
                                        "original-status": (_unref(donationStore).currentDonation?.status ?? state.status),
                                        "original-payment-method": _unref(donationStore).currentDonation?.paymentMethod,
                                        locked: isDonationLocked.value,
                                        "available-transitions": _unref(donationStore).availableTransitions,
                                        "transitions-loading": _unref(donationStore).isLoadingTransitions,
                                        "transitions-error": _unref(donationStore).transitionsError,
                                        "onUpdate:state": _cache[5] || (_cache[5] = ($event) => (Object.assign(state, $event)))
                                    }, null, 8 /* PROPS */, ["state", "original-status", "original-payment-method", "locked", "available-transitions", "transitions-loading", "transitions-error"]))
                                    : (activeTab.value === 'allocations')
                                        ? (_openBlock(), _createBlock(DonationTabAllocations, {
                                            key: 2,
                                            "donation-id": _unref(route).params.donationId,
                                            allocations: allocations.value,
                                            "is-fully-allocated": isDonationFullyAllocated.value,
                                            locked: isDonationLocked.value,
                                            loading: isLoadingAllocations.value,
                                            onRefresh: loadAllocations
                                        }, null, 8 /* PROPS */, ["donation-id", "allocations", "is-fully-allocated", "locked", "loading"]))
                                        : (activeTab.value === 'taxReceipt')
                                            ? (_openBlock(), _createBlock(DonationTabTaxReceipt, {
                                                key: taxReceiptKey.value,
                                                "donation-id": _unref(route).params.donationId,
                                                currency: state.currency,
                                                "donation-type": state.donationType ?? 'OneOff',
                                                "donation-status": currentDonationStatus.value,
                                                "contact-id": _unref(donationStore).currentDonation?.contactId || undefined,
                                                "organization-id": _unref(donationStore).currentDonation?.donorType === 'Organization'
                                                    ? _unref(donationStore).currentDonation.organizationId || undefined
                                                    : undefined,
                                                onReceiptUpdated: onReceiptUpdated
                                            }, null, 8 /* PROPS */, ["donation-id", "currency", "donation-type", "donation-status", "contact-id", "organization-id"]))
                                            : (activeTab.value === 'comments')
                                                ? (_openBlock(), _createBlock(DonationTabNotes, {
                                                    key: 4,
                                                    "donation-id": _unref(route).params.donationId,
                                                    comments: _unref(donationStore).comments
                                                }, null, 8 /* PROPS */, ["donation-id", "comments"]))
                                                : (activeTab.value === 'history')
                                                    ? (_openBlock(), _createBlock(DonationTabHistory, {
                                                        key: 5,
                                                        history: changeHistory.value,
                                                        loading: isLoadingHistory.value,
                                                        currency: state.currency
                                                    }, null, 8 /* PROPS */, ["history", "loading", "currency"]))
                                                    : _createCommentVNode("v-if", true)
                        ], 8 /* PROPS */, _hoisted_11))
                        : _createCommentVNode("v-if", true)
                ]),
                _createVNode(_unref(UnsavedChangesModal), {
                    modelValue: showUnsavedChangesModal.value,
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => ((showUnsavedChangesModal).value = $event)),
                    title: _ctx.$t('leaveWithoutSaving.title'),
                    message: _ctx.$t('leaveWithoutSaving.message'),
                    "stay-label": _ctx.$t('leaveWithoutSaving.stay'),
                    "leave-label": _ctx.$t('leaveWithoutSaving.leave'),
                    "save-label": _ctx.$t('leaveWithoutSaving.save'),
                    "save-disabled": !canSave.value || isSaving.value,
                    onDiscard: handleLeave,
                    onSave: handleSaveAndContinue,
                    onStay: handleStay
                }, null, 8 /* PROPS */, ["modelValue", "title", "message", "stay-label", "leave-label", "save-label", "save-disabled"]),
                _createVNode(_unref(ConfirmDeleteModal), {
                    modelValue: showDeleteModal.value,
                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event) => ((showDeleteModal).value = $event)),
                    title: _ctx.$t('finance.donations.deleteConfirmTitle'),
                    subtitle: _ctx.$t('finance.donations.deleteConfirmMessage'),
                    "confirm-label": _ctx.$t('finance.donations.detail.delete'),
                    "cancel-label": _ctx.$t('finance.donations.detail.cancel'),
                    onConfirm: handleDelete
                }, null, 8 /* PROPS */, ["modelValue", "title", "subtitle", "confirm-label", "cancel-label"]),
                (deleteBlocker.value)
                    ? (_openBlock(), _createBlock(DonationDeleteBlockedModal, {
                        key: 0,
                        modelValue: showDeleteBlockedModal.value,
                        "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event) => ((showDeleteBlockedModal).value = $event)),
                        blocker: deleteBlocker.value
                    }, null, 8 /* PROPS */, ["modelValue", "blocker"]))
                    : _createCommentVNode("v-if", true)
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
